import React from 'react';
import { Target, Eye, Award } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { mockData } from '../data/mock';

export const About = () => {
  const sections = [
    {
      icon: Eye,
      title: 'Vision',
      description: 'To lead in providing innovative, sustainable, and integrated engineering solutions that contribute to a more resilient, technologically empowered, and environmentally responsible Timor-Leste.',
      color: '#003366'
    },
    {
      icon: Target,
      title: 'Mission',
      description: 'To deliver high-quality ICT, renewable energy, civil works, and WASH solutions tailored to community and institutional needs. To expand local capacity through training, technology transfer, and sustainable practices. To ensure all projects meet international standards for safety, efficiency, and environmental stewardship. To support national development by providing reliable, future-ready systems.',
      color: '#339933'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-white via-[#176472]/5 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-4">
            About <span className="bg-gradient-to-r from-[#003366] via-[#339933] to-[#003366] bg-clip-text text-transparent">ELZALIRA</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Elzalira is a multidisciplinary engineering and technology company delivering integrated solutions across ICT, renewable energy, civil works, and water and sanitation services in Timor-Leste. With a strong focus on innovation, sustainability, and capacity-building, the company helps public institutions, development partners, and private organizations implement reliable, efficient, and future-ready systems.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mt-4">
            Elzalira's team combines technical expertise with practical field experience to provide ICT maintenance strategies, solar PV system design and installation, structural rehabilitation works, and community-centered WASH solutions. Through high-quality engineering practices and a commitment to professional integrity, Elzalira ensures that each project is delivered with precision, sustainability, and long-term impact. The company is dedicated to supporting Timor-Leste's development goals by empowering communities, strengthening institutions, and promoting environmentally responsible infrastructure.
          </p>
        </div>

        {/* Vision & Mission Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Card
                key={index}
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden group bg-white"
              >
                <div
                  className="h-2 w-full transition-all duration-300 group-hover:h-3"
                  style={{ background: `linear-gradient(to right, ${section.color}, #00B050)` }}
                ></div>
                <CardContent className="p-8">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${section.color}, #00B050)` }}
                  >
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">{section.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-lg">{section.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Core Values */}
        <div className="bg-gradient-to-br from-[#1E4E9E]/10 via-white to-[#00B050]/10 rounded-3xl p-8 lg:p-12 shadow-xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#003366] to-[#339933] rounded-2xl shadow-xl mb-4">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-4xl font-bold text-slate-900 mb-2">Our Core Values</h3>
            <p className="text-slate-600 text-lg">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-t-4 border-[#00B050] hover:border-[#003366]"
              >
                <h4 className="text-xl font-bold bg-gradient-to-r from-[#003366] to-[#339933] bg-clip-text text-transparent mb-3">{value.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};